import { DimensionType } from '../../types/field';
import { QuerySourceType } from '../../types/querySources';
import { getColumnAxisType, VizAggregationOptions, } from '../../visualizations/types';
const KIND_ORDER = [
    'table',
    'bar',
    'horizontal',
    'line',
    'scatter',
    'pie',
    'funnel',
    'big_number',
];
const isNumeric = (column) => column.type === DimensionType.NUMBER;
const isTemporal = (column) => column.type === DimensionType.DATE ||
    column.type === DimensionType.TIMESTAMP;
const firstOfType = (columns, type) => columns.find((column) => column.type === type);
/** Mirrors the SQL runner default: first date/timestamp, else string, else boolean. */
const pickX = (columns) => columns.find(isTemporal) ??
    firstOfType(columns, DimensionType.STRING) ??
    firstOfType(columns, DimensionType.BOOLEAN);
const pickY = (columns, x) => columns.find((column) => isNumeric(column) && column.reference !== x?.reference);
/** Semantic-layer nodes declare their axes: first dimension on x, first metric on y. */
const pickSemanticAxes = (columns, node) => {
    if (node?.sourceType !== QuerySourceType.SEMANTIC_LAYER)
        return undefined;
    const byReference = new Map(columns.map((column) => [column.reference, column]));
    const x = node.dimensions
        .map((fieldId) => byReference.get(fieldId))
        .find((column) => column !== undefined && !isNumeric(column));
    const y = node.metrics
        .map((fieldId) => byReference.get(fieldId))
        .find((column) => column !== undefined && isNumeric(column));
    if (!x && !y)
        return undefined;
    return { x: x ?? pickX(columns), y: y ?? pickY(columns, x) };
};
const hasDuplicateValues = (rows, reference) => {
    const seen = new Set();
    return rows.some((row) => {
        const value = row[reference];
        if (seen.has(value))
            return true;
        seen.add(value);
        return false;
    });
};
/** Kinds a node result can render as and the one it opens with, from column types and row shape alone. */
export const getComposerVizPlan = ({ columns, rows, node, }) => {
    const semantic = pickSemanticAxes(columns, node);
    const x = semantic?.x ?? pickX(columns);
    const y = semantic?.y ?? pickY(columns, x);
    const numerics = columns.filter(isNumeric);
    const axes = {};
    if (x && y) {
        axes.bar = { x, y };
        axes.horizontal = { x, y };
        axes.line = { x, y };
    }
    if (numerics.length >= 2) {
        axes.scatter = { x: numerics[0], y: numerics[1] };
    }
    const category = x?.type === DimensionType.STRING
        ? x
        : firstOfType(columns, DimensionType.STRING);
    // Pie and funnel slice the string column; y stays unless it is that column.
    const categoryValue = (() => {
        if (!category)
            return undefined;
        if (y && y.reference !== category.reference)
            return y;
        return pickY(columns, category);
    })();
    if (category &&
        categoryValue &&
        !hasDuplicateValues(rows, category.reference)) {
        axes.pie = { x: category, y: categoryValue };
        axes.funnel = { x: category, y: categoryValue };
    }
    if (rows.length === 1 && numerics.length >= 1) {
        axes.big_number = { x: null, y: y ?? numerics[0] };
    }
    const availableKinds = KIND_ORDER.filter((kind) => kind === 'table' || axes[kind] !== undefined);
    const defaultKind = (() => {
        if (axes.big_number)
            return 'big_number';
        if (!x || !y || hasDuplicateValues(rows, x.reference))
            return 'table';
        return isTemporal(x) ? 'line' : 'bar';
    })();
    return { availableKinds, defaultKind, axes };
};
/** Chart data straight from the fetched rows: x as the index (none for a big number), y as the value. No aggregation, no server call. */
export const buildComposerChartData = ({ rows, x, y, }) => {
    const index = x
        ? { reference: x.reference, type: getColumnAxisType(x.type) }
        : undefined;
    return {
        data: {
            queryUuid: undefined,
            fileUrl: undefined,
            results: rows.map((row) => ({
                ...(x ? { [x.reference]: row[x.reference] } : {}),
                [y.reference]: row[y.reference],
            })),
            indexColumn: index,
            valuesColumns: [
                {
                    referenceField: y.reference,
                    pivotColumnName: y.reference,
                    aggregation: VizAggregationOptions.ANY,
                    pivotValues: [],
                },
            ],
            columns: [
                ...(x ? [{ reference: x.reference, type: x.type }] : []),
                { reference: y.reference, type: y.type },
            ],
            columnCount: x ? 2 : 1,
        },
        layout: {
            x: index,
            y: [
                {
                    reference: y.reference,
                    aggregation: VizAggregationOptions.ANY,
                },
            ],
            groupBy: [],
        },
    };
};
//# sourceMappingURL=composerViz.js.map